/**
 * Which way a maneuver actually turns, measured rather than assumed.
 *
 * The motivating failure, reported from the road: "many times U turn on multi
 * lane roads are on the right, but the actual U turn is on the left, not fun at
 * all." The app was deciding the direction of a U-turn from the country's
 * driving side - left-hand traffic, therefore swing right through the median.
 * That is true of an ordinary divided road and false of the many Thai highways
 * where the U-turn is a dedicated left-side loop or underpass, built that way
 * precisely because crossing the median at that speed would be lethal.
 *
 * A country-level rule cannot know which of those two a given junction is. The
 * route geometry can: it traces the path the vehicle is about to drive. So we
 * measure the curve and use that, and keep the driving-side rule only for the
 * case where the geometry is too coarse to read.
 *
 * The same measurement fixes ambiguous sharp turns, where Mapbox's `modifier`
 * rounds a 150-degree hairpin to "sharp left" but the geometry shows the swing.
 */

import type { LngLat, RouteStep } from '../core/types';
import { bearing, bearingDelta, distance } from '../core/geo';

/** How far either side of the maneuver to look, metres. */
const ARM_M = 45;
/** Below this total turn we cannot call it - report unknown rather than guess. */
const MIN_TURN_DEG = 25;

/**
 * Signed swing through the junction between two steps.
 *
 * `prev` is the step arriving at the maneuver, `step` the one leaving it. The
 * turn happens at the join, so the arms come from the END of `prev` and the
 * START of `step`.
 *
 * Returns a negative number for a left swing, positive for right, and 0 when
 * there is not enough geometry to be sure. Magnitude is degrees turned.
 */
export function turnSign(prev: RouteStep | null, step: RouteStep | null): number {
  const inbound = tailBearing(prev?.geometry?.coordinates ?? []);
  const outbound = headBearing(step?.geometry?.coordinates ?? []);
  if (inbound == null || outbound == null) return 0;

  const delta = bearingDelta(inbound, outbound);
  if (Math.abs(delta) < MIN_TURN_DEG) return 0;
  return delta;
}

/**
 * Swing measured across a U-turn specifically.
 *
 * A U-turn reverses direction, so the in/out bearings differ by roughly 180
 * degrees and their signed difference is numerically unstable - a hair either
 * side of straight-back flips the sign. The reliable signal is the LATERAL
 * displacement: where the vehicle ends up relative to the direction it was
 * travelling. Swing right through a median gap and you finish to the right of
 * your approach line; take a left-side loop and you finish to the left.
 *
 * Negative is left, positive is right, 0 unknown.
 */
export function uturnSign(prev: RouteStep | null, step: RouteStep | null): number {
  const approach = prev?.geometry?.coordinates ?? [];
  const exit = step?.geometry?.coordinates ?? [];
  if (approach.length < 2 || exit.length < 2) return 0;

  const pivot = approach[approach.length - 1];
  const heading = tailBearing(approach);
  if (heading == null) return 0;

  /*
   * Largest sideways offset over the first stretch of the new road, in metres.
   *
   * Working in degrees means the interesting cases all sit near +/-180, where
   * the sign is one rounding error from flipping. Metres sideways has no such
   * problem: it is signed by construction and "we end up 9 m right of the
   * approach line" is a claim you can check against the real junction.
   *
   * The MAXIMUM over the path rather than one sampled point, because the two
   * cases have different shapes. A median gap holds a small steady offset the
   * whole way; a left-side loop bulges well out and comes back. Sampling one
   * point can land in the middle of that bulge or at the end of it, and the
   * two readings disagree - so take the widest excursion, which is the thing
   * that actually distinguishes them.
   */
  let perpendicular = 0;
  for (const point of exit) {
    const away = distance(pivot, point);
    if (away < 6) continue;
    if (away > 150) break;
    const lateral = bearingDelta(heading, bearing(pivot, point));
    const offset = away * Math.sin((lateral * Math.PI) / 180);
    if (Math.abs(offset) > Math.abs(perpendicular)) perpendicular = offset;
  }

  // Under half a lane either way, the geometry is not telling us anything.
  if (Math.abs(perpendicular) < 2.5) return 0;
  return perpendicular;
}

/** Bearing over the last stretch of a line, or null if it is too short. */
function tailBearing(coords: LngLat[]): number | null {
  if (coords.length < 2) return null;
  const end = coords[coords.length - 1];
  for (let i = coords.length - 2; i >= 0; i--) {
    if (distance(coords[i], end) >= ARM_M) return bearing(coords[i], end);
  }
  return bearing(coords[0], end);
}

/** Bearing over the first stretch of a line, or null if it is too short. */
function headBearing(coords: LngLat[]): number | null {
  if (coords.length < 2) return null;
  const start = coords[0];
  for (let i = 1; i < coords.length; i++) {
    if (distance(start, coords[i]) >= ARM_M) return bearing(start, coords[i]);
  }
  return bearing(start, coords[coords.length - 1]);
}


/**
 * Side a U-turn is taken on, for lane guidance and the spoken instruction.
 * Falls back to the driving-side rule when the geometry cannot say.
 */
export function uturnSide(
  prev: RouteStep | null,
  step: RouteStep | null,
  drivingSide: 'left' | 'right',
): 'left' | 'right' {
  const sign = uturnSign(prev, step);
  if (sign < 0) return 'left';
  if (sign > 0) return 'right';
  return drivingSide === 'left' ? 'right' : 'left';
}
